<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("damage_item_entry") }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/damage/item/list" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="submitDamageItem" enctype="multipart/form-data">
        <!-- Header Form Section -->
        <div class="row gy-3 gx-4 mb-3 damage-header-fields">
          <div class="col-md-6">
            <div class="damage-field-row">
              <label class="damage-field-label">{{$t('voucher_no')}}:</label>
              <div class="damage-field-control">
                <input type="text" class="form-control" v-model="item.receiving_no" readonly/>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="damage-field-row">
              <label class="damage-field-label">{{$t('voucher_date')}}:</label>
              <div class="damage-field-control">
                <input type="date" class="form-control" v-model="item.receiving_date" @change="fetchReceivingNo" />
                <div v-if="errors.receiving_date" class="text-danger mt-1 small">
                  <span>{{errors.receiving_date}}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="damage-field-row damage-field-row-top">
              <label class="damage-field-label">{{$t('search_item')}}:</label>
              <div class="damage-field-control position-relative">
                <input
                  ref="itemSearch"
                  type="text"
                  autocomplete="off"
                  v-model="query"
                  @input="onSearchInput"
                  :placeholder="$t('search_item_by_name_barcode_serial')"
                  class="form-control search-input-with-clear"
                  @keydown="handleKeys"
                />
                <button
                  v-if="query || results.length"
                  type="button"
                  class="search-clear"
                  :title="$t('clear') || 'Clear'"
                  @click="clearItemSearch"
                >
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>

                <div
                  v-if="results.length"
                  class="search-results shadow rounded bg-white position-absolute mt-1"
                  style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                >
                  <ul class="list-group list-group-flush">
                    <li
                      v-for="(item, index) in results"
                      :key="item.serialized_unit_id || `${item.id}-${item.variant_id || 0}-${index}`"
                      class="list-group-item d-flex justify-content-between align-items-center"
                      :class="{ 'bg-primary text-white': index === itemIndex }"
                      style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                    >
                      <div
                        class="flex-grow-1 d-flex flex-column"
                        @click="selectItem(item)"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div
                            style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;"
                          >
                            <img
                              :src="$getRowImageUrl(item)"
                              style="width:100%; height:100%; object-fit:cover;"
                            />
                          </div>

                          <div class="d-flex flex-column">
                            <strong class="text-dark">
                              {{ formatSearchItemTitle(item) }}
                            </strong>

                            <span
                              v-if="item.serial_number"
                              style="font-size: 0.75rem; color: black;"
                            >
                              {{ $t('serial_number') }}: {{ item.serial_number }}
                            </span>

                            <span
                              v-if="!item.serialized_unit_id"
                              style="font-size: 0.75rem; color: black;"
                            >
                              Stock: {{ getAvailableStock(item) }}
                            </span>

                            <span
                              v-if="!item.serialized_unit_id"
                              style="font-size: 0.75rem; color: black;"
                            >
                              {{ $t('selling_price') }}: {{ formatMoney(item.selling_price ?? 0) }}
                            </span>

                            <span
                              v-if="item.sku"
                              style="font-size: 0.75rem; color: black;"
                            >
                              SKU: {{ item.sku }}
                            </span>

                            <span
                              v-if="item.barcode_qr_code"
                              style="font-size: 0.75rem; color: black;"
                            >
                              Barcode: {{ item.barcode_qr_code }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <div v-if="!results.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                    No results found
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="damage-field-row damage-field-row-top">
              <label class="damage-field-label">
                {{ $t('warehouse_name') }}: <span class="text-danger">*</span>
              </label>
              <div class="damage-field-control">
                <FormDropdown
                  v-model="item.warehouse_id"
                  :options="formattedwarehouses"
                  clearable
                />
                <div class="text-danger mt-1" style="height:1.2em; font-size:0.875rem;">
                  <span v-if="errors.warehouse_id">{{ errors.warehouse_id }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- item Info Input Section -->
        <div class="row gy-3 gx-4 mb-3 p-3 mx-1 border rounded shadow-sm damage-line-fields">
          <div class="col-md-6">
            <div class="damage-field-row damage-field-row-top">
              <label class="damage-field-label">
                {{$t('narration')}}: <span class="text-danger">*</span>
              </label>
              <div class="damage-field-control">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Narration"
                  v-model="item.narration"
                />
                <div class="text-danger mt-1" style="height:1.2em; font-size:0.875rem;">
                  <span v-if="errors.narration">{{ errors.narration }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="damage-field-row damage-field-row-top">
              <label class="damage-field-label">
                {{$t('quantity')}}: <span class="text-danger">*</span>
              </label>
              <div class="damage-field-control">
                <div class="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    class="form-control"
                    placeholder="Quantity"
                    v-model="item.quantity"
                    :readonly="selectedItem?.item_type_code === 'WARRANTY'"
                  />
                  <button type="button" class="btn btn-info flex-shrink-0" @click="addItem">
                    {{$t('add')}}
                  </button>
                </div>
                <div class="text-danger mt-1" style="font-size:0.875rem; height: 1.2em;">
                  <span v-if="errors.quantity">{{ errors.quantity }}</span>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="selectedItem?.item_type_code === 'WARRANTY'"
            class="col-md-12"
          >
            <div class="damage-field-row damage-field-row-top">
              <label class="damage-field-label">
                {{ $t('serial_numbers') }}: <span class="text-danger">*</span>
              </label>
              <div class="damage-field-control">
                <textarea
                  class="form-control"
                  rows="2"
                  v-model="item.serial_numbers_text"
                  :placeholder="$t('serial_numbers_hint')"
                  @input="onSerialTextInput"
                ></textarea>
                <small class="text-muted d-block mb-2">{{ $t('serial_pick_or_scan') }}</small>

                <div
                  v-if="availableSerials.length"
                  class="damage-serial-picker border rounded p-2"
                >
                  <div class="small text-muted mb-1">
                    {{ $t('available_serials') || 'Available serials' }}
                    ({{ availableSerials.length }})
                  </div>
                  <label
                    v-for="serial in availableSerials"
                    :key="serial.id"
                    class="d-inline-flex align-items-center me-3 mb-1 small"
                  >
                    <input
                      type="checkbox"
                      class="me-1"
                      :value="serial.serial_number"
                      :checked="isSerialSelected(serial.serial_number)"
                      @change="toggleSerial(serial.serial_number, $event.target.checked)"
                    />
                    {{ serial.serial_number }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive mb-3 fixed-table">
          <table class="table table-bordered table-striped align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 30%">{{$t('item_name')}}</th>
                <th style="width: 30%">{{$t('narration')}}</th>
                <th style="width: 30%">{{$t('quantity')}}</th>
                <th style="width: 10%" >{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cust, index) in addedItems" :key="index">
                <td>{{ cust.item_name }}</td>
                <td>
                  <div>{{ cust.narration }}</div>
                  <small
                    v-if="cust.serial_numbers_text"
                    class="text-muted"
                  >
                    {{ $t('serial_number') }}:
                    {{ formatGridSerials(cust.serial_numbers_text) }}
                  </small>
                </td>
                <td>{{ cust.quantity }}</td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeItem(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <!-- Show empty row if no data -->
              <tr v-if="!addedItems.length">
                <td colspan="4" class="text-center text-muted">No Items added</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="row g-3 align-items-end mt-4">
          <div class="col-auto d-flex align-items-end ms-auto">
            <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
            <button type="submit" class="btn btn-submit" :disabled="saving">{{ $t('submit') }}</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';
import debounce from 'lodash.debounce'
import { truncateMoney2, todayLocal } from '@/utils';
import { apiErrorMessage } from '@/helpers/apiError';

export default {
  components: {
    Vue3Datepicker,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      item: {
        item: null, narration: '', quantity: '', receiving_no: '', receiving_date: todayLocal(),
        stock_quantity: '', warehouse_id: '', item_account_id: '', variant_id: null,
        serial_numbers_text: '',
      },

      errors: {},
      errorMessage: "",
      saving: false,
      query: "",
      results: [],
      itemIndex: 0,
      selectedItem: null,
      addedItems: [],
      warehouses: [],
      debouncedSearch: null,
      warehouse_qty: 0,
      selectedParentItem: null,
      availableSerials: [],
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchReceivingNo();
  },

  computed: {
    formattedwarehouses() {
    return (this.warehouses || [])
      .filter(w => w.id !== undefined && w.name)
      .map(w => ({
        id: w.id.toString(),
        text: w.name.toString()
      }));
    }
  },

  methods: {
    formatSearchItemTitle(item) {
      if (!item) return '';
      const code = item.item_code ? ` - ${item.item_code}` : '';
      const attrs = (item.variant_attributes || [])
        .map((v) => {
          const name = v.attribute || v.attribute_name || '';
          const value = v.value || v.attribute_value || '';
          return name && value ? `${name}: ${value}` : (value || name);
        })
        .filter(Boolean)
        .join(' | ');
      const variant = attrs ? ` (${attrs})` : '';
      return `${item.item_name || ''}${variant}${code}`;
    },

    // MULTI_VARIANT, or WARRANTY (or any type) that already has variants.
    isVariantParent(item) {
      if (!item || item.variant_id || item.variant_attributes?.length || item.serialized_unit_id) {
        return false;
      }
      return item.item_type_code === 'MULTI_VARIANT' || !!item.has_variants;
    },

    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    parseSerialNumbers(text) {
      return String(text || '')
        .replace(/,/g, '\n')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    },

    formatGridSerials(text) {
      return this.parseSerialNumbers(text).join(', ');
    },

    isSerialSelected(serial) {
      const key = String(serial || '').toLowerCase();
      return this.parseSerialNumbers(this.item.serial_numbers_text)
        .some((s) => s.toLowerCase() === key);
    },

    setSerialNumbers(serials) {
      const unique = [];
      const seen = new Set();
      for (const raw of serials || []) {
        const serial = String(raw || '').trim();
        if (!serial) continue;
        const key = serial.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(serial);
      }
      this.item.serial_numbers_text = unique.join('\n');
      if (this.selectedItem?.item_type_code === 'WARRANTY') {
        this.item.quantity = unique.length || '';
      }
    },

    appendSerial(serial) {
      const list = this.parseSerialNumbers(this.item.serial_numbers_text);
      const key = String(serial || '').trim().toLowerCase();
      if (!key) return;
      if (!list.some((s) => s.toLowerCase() === key)) {
        list.push(String(serial).trim());
      }
      this.setSerialNumbers(list);
    },

    toggleSerial(serial, checked) {
      const list = this.parseSerialNumbers(this.item.serial_numbers_text);
      const key = String(serial || '').toLowerCase();
      if (checked) {
        if (!list.some((s) => s.toLowerCase() === key)) {
          list.push(serial);
        }
      } else {
        const next = list.filter((s) => s.toLowerCase() !== key);
        this.setSerialNumbers(next);
        return;
      }
      this.setSerialNumbers(list);
    },

    onSerialTextInput() {
      if (this.selectedItem?.item_type_code === 'WARRANTY') {
        this.item.quantity = this.parseSerialNumbers(this.item.serial_numbers_text).length || '';
      }
    },

    async fetchDropdownData() {
      try {
        const [
          receive_noRes, warehouseRes
        ] = await Promise.all([
          this.$axios.get(apiUrls.damage_receive_number.get),
        ]);

        this.item.receiving_no = receive_noRes?.data?.receiving_no || '';

      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    },

    onSearchInput() {
      // Typing cleared the field → hide the list (unless ArrowDown asked for all).
      if (!String(this.query || '').trim()) {
        this.results = [];
        this.itemIndex = 0;
        return;
      }
      this.debouncedSearch();
    },

    clearItemSearch({ keepFocus = true } = {}) {
      this.query = '';
      this.results = [];
      this.itemIndex = 0;
      if (keepFocus) {
        this.$nextTick(() => this.$refs.itemSearch?.focus());
      }
    },

    async searchItems(options = {}) {
      const listAll = Boolean(options && options.listAll);
      const q = String(this.query || '').trim();
      if (!listAll && !q) {
        this.results = [];
        return;
      }

      try {
        // While a warranty item/variant is selected, filter its available serials in the dropdown.
        if (
          !listAll
          && this.selectedItem?.item_type_code === 'WARRANTY'
          && this.availableSerials.length
          && !this.isVariantParent(this.selectedItem)
        ) {
          const filtered = this.availableSerials.filter((u) =>
            String(u.serial_number || '').toLowerCase().includes(q.toLowerCase())
          );
          if (filtered.length) {
            this.results = filtered.map((unit) => this.serialToSearchRow(this.selectedItem, unit));
            this.itemIndex = 0;
            const exact = filtered.find(
              (u) => String(u.serial_number).toLowerCase() === q.toLowerCase()
            );
            if (exact) {
              await this.selectItem(this.serialToSearchRow(this.selectedItem, exact));
            }
            return;
          }
        }

        // Direct serial / barcode search (POS search supports warranty serials + ↓ list-all).
        const itemSearch = await this.$axios.get(apiUrls.item_search.get, {
          params: { q: listAll ? '' : q },
        });
        const searchResults = itemSearch.data.results || [];
        if (!listAll) {
          const exactSerial = searchResults.find(
            (r) =>
              r.serialized_unit_id
              && r.serial_number
              && String(r.serial_number).toLowerCase() === q.toLowerCase()
          );
          if (exactSerial) {
            await this.selectItem(exactSerial);
            return;
          }
        }

        const response = await this.$axios.get(apiUrls.batch_item_search.get, {
          params: { q: listAll ? '' : q },
        });
        let results = response.data.results || [];

        // Merge any serial hits from item search that are not exact (partial).
        const serialHits = searchResults.filter((r) => r.serialized_unit_id && r.serial_number);
        if (serialHits.length) {
          const keys = new Set(results.map((r) => `i${r.id}`));
          for (const hit of serialHits) {
            const key = `s${hit.serialized_unit_id}`;
            if (!keys.has(key)) {
              results = [hit, ...results];
              keys.add(key);
            }
          }
        }

        this.results = results;
        this.itemIndex = 0;

        if (
          !listAll
          && results.length === 1
          && results[0].barcode_qr_code
          && results[0].barcode_qr_code === q
        ) {
          await this.selectItem(results[0]);
          return;
        }

        await this.$nextTick(() => {
          if (window.feather) feather.replace();
        });
      } catch (error) {
        console.error('Search error:', error);
        this.results = [];
      }
    },

    getAvailableStock(item) {
      if (!item) return 0;

      // Variant
      if (item.variant_id) {
        return Number(item.current_stock ?? 0);
      }

      // Normal item
      return Number(
        item.stock_quantity ??
        item.stock_qty ??
        item.current_stock ??
        0
      );
    },

    serialToSearchRow(item, unit) {
      return {
        ...item,
        id: item.item_id || item.id,
        item_id: item.item_id || item.id,
        variant_id: unit.variant_id || item.variant_id || null,
        item_type_code: 'WARRANTY',
        serialized_unit_id: unit.id,
        serial_number: unit.serial_number,
        warranty_period_days: unit.warranty_period_days || item.warranty_period_days,
        current_stock: 1,
        stock_qty: 1,
        stock_quantity: 1,
        has_variants: false,
      };
    },

    async applySelectedItem(item, { clearSerials = true } = {}) {
      const enriched = {
        ...item,
        item_account_id:
          item.item_account_id
          || this.selectedParentItem?.item_account_id
          || this.selectedItem?.item_account_id
          || null,
      };
      this.selectedItem = enriched;
      this.query = this.formatSearchItemTitle(enriched);
      this.item.item = enriched.item_id || enriched.id;
      this.item.stock_quantity = this.getAvailableStock(enriched);
      this.item.variant_id = enriched.variant_id || null;
      if (clearSerials) {
        this.item.serial_numbers_text = '';
        this.item.quantity = enriched.item_type_code === 'WARRANTY' ? '' : this.item.quantity;
      }
      await this.fetchWarehouses();
    },

    async selectItem(item) {
      if (this.isVariantParent(item)) {
        this.selectedParentItem = item;
        this.availableSerials = [];
        await this.loadVariants(item.id || item.item_id);
        return;
      }

      // Picked a serial from search / list → keep item selected and fill Serial numbers.
      if (item.serialized_unit_id && item.serial_number) {
        const sameItem =
          this.selectedItem
          && (this.selectedItem.item_id || this.selectedItem.id) === (item.item_id || item.id)
          && Number(this.selectedItem.variant_id || 0) === Number(item.variant_id || 0);

        if (!sameItem) {
          await this.applySelectedItem(item, { clearSerials: true });
          await this.loadAvailableSerials(item);
        }
        this.appendSerial(item.serial_number);
        this.results = [];
        this.itemIndex = 0;
        this.query = this.formatSearchItemTitle(this.selectedItem || item);
        return;
      }

      await this.applySelectedItem(item, { clearSerials: true });
      this.results = [];
      this.itemIndex = 0;

      if (item.item_type_code === 'WARRANTY') {
        await this.loadAvailableSerials(item);
      } else {
        this.availableSerials = [];
      }
    },

    async handleKeys(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        // First ↓ with an empty list loads all items (and serial matches when searching).
        if (!this.results.length) {
          await this.searchItems({ listAll: true });
          return;
        }
        this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        const selectedProduct = this.results[this.itemIndex];
        if (selectedProduct) {
          this.selectItem(selectedProduct);
        }
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.clearItemSearch();
        event.preventDefault();
      }
    },

    async loadVariants(itemId) {
      try {
        const response = await this.$axios.get(
          `/item/${itemId}/variants/`
        );

        const parentType = this.selectedParentItem?.item_type_code || null;
        const variants = response.data.results || [];
        // Keep WARRANTY (etc.) on variant rows so serial UI and title formatting work.
        this.results = variants.map((v) => ({
          ...v,
          item_type_code: parentType || v.item_type_code,
          has_variants: false,
        }));
        this.existingVariants = this.results;
        this.itemIndex = 0;

        if (!this.results.length) {
          // WARRANTY parent with no variants → treat as leaf and load serials.
          if (parentType === 'WARRANTY' && this.selectedParentItem) {
            await this.selectItem({
              ...this.selectedParentItem,
              has_variants: false,
            });
            return;
          }
          this.toast.warning(this.$t('no_variants_found') || 'No variants found for this item.');
        }
      } catch (error) {
        console.error(error);
      }
    },

    async loadAvailableSerials(item) {
      const itemId = item.item_id || item.id;
      if (!itemId) {
        this.availableSerials = [];
        return;
      }
      try {
        const params = {
          item_id: itemId,
          status: 'in_stock',
        };
        if (item.variant_id) {
          params.variant_id = item.variant_id;
        }
        const { data } = await this.$axios.get(apiUrls.serialized_units.get, { params });
        let units = data.results || [];
        if (this.item.warehouse_id) {
          units = units.filter(
            (u) => Number(u.warehouse_id || 0) === Number(this.item.warehouse_id)
          );
        }
        this.availableSerials = units;

        // Also show serials in the search dropdown for quick click-add.
        this.results = units.map((unit) => this.serialToSearchRow(item, unit));
        this.itemIndex = 0;
        if (!String(this.query || '').trim()) {
          this.query = this.formatSearchItemTitle(item);
        }

        if (!units.length) {
          this.toast.warning(
            this.$t('no_unsold_serials')
            || 'No unsold serial numbers available for this product.'
          );
        }
      } catch (error) {
        console.error(error);
        this.availableSerials = [];
        this.toast.error('Failed to load serial numbers.');
      }
    },

    async addItem() {
      if (!this.selectedItem) {
        this.toast.warning("Please select Item first.");
        return;
      }

      const availableStock = this.getAvailableStock(this.selectedItem);

      const currentItemId = this.selectedItem.item_id || this.selectedItem.id;
      const currentVariantId = this.selectedItem.variant_id || null;
      const currentItemType = this.selectedItem.item_type_code;

      const alreadyExists = this.addedItems.some(item => {
        const itemId = item.item_id || item.item;
        const variantId = item.variant_id || null;

        if (currentVariantId) {
            return variantId === currentVariantId;
        }

        return itemId === currentItemId && !variantId;
      });

      if (alreadyExists) {
        this.toast.warning("This item/variant has already been added.");

        this.query = "";
        this.results = [];
        this.selectedItem = null;
        this.itemIndex = 0;
        return;
      }

      // // Prevent duplicate customer
      // const exists = this.addedItems.some(
      //   c => c.id === this.selectedItem.id
      // );
      //
      // if (exists) {
      //   this.toast.info("Item already added.");
      //   return;
      // }

      // Frontend validation before adding to list
      this.errors = {};
      const requiredFields = [
        "item", "narration", "receiving_date", "warehouse_id", "quantity"
      ];
      requiredFields.forEach(field => {
        if (!this.item[field]) this.errors[field] = this.$t('error.required');
      });

      if (Object.keys(this.errors).length > 0) {
        return;
      }

      // item quantity validation.
      const inputQty = Number(this.item.quantity || 0);

      if (inputQty <= 0) {
        this.toast.warning(`Invalid quantity for item "${this.selectedItem.item_name}".`);
        return;
      }

      // Damage comes out of the selected warehouse, so it is capped by that warehouse's stock.
      const availableQty = Math.min(availableStock, Number(this.warehouse_qty || 0));
      if (inputQty > availableQty) {
        this.toast.warning(`Exceeds stock of "${this.selectedItem.item_name}" in the selected warehouse. Only ${availableQty} available.`);
        return;
      }

      let serialIds = [];
      if (this.selectedItem.item_type_code === 'WARRANTY') {
        try {
          serialIds = await this.resolveSerialIds(
            this.selectedItem.item_id || this.selectedItem.id,
            this.selectedItem.variant_id,
            this.item.serial_numbers_text,
            inputQty,
          );
        } catch (err) {
          this.toast.warning(err.message || String(err));
          return;
        }
      }

      // Store full item data in addedItems
      const newItem = {
        ...this.item,
        id: this.selectedItem.id,
        item_id: this.selectedItem.item_id || this.selectedItem.id,
        variant_id: this.selectedItem.variant_id || null,
        item_account_id: this.selectedItem.item_account_id || this.selectedParentItem.item_account_id,
        item_name: this.selectedItem.item_name,
        item_code: this.selectedItem.item_code,
        stock_quantity: this.selectedItem.stock_quantity,
        images: this.selectedItem.images || [],
        serial_ids: serialIds,
        item_type_code: this.selectedItem.item_type_code,
      };

      this.addedItems.push(newItem);

      // Reset only row fields, not whole form structure
      this.item = {
        item: null,
        narration: '',
        quantity: '',
        receiving_no: this.item.receiving_no,
        receiving_date: this.item.receiving_date,
        stock_quantity: '',
        warehouse_id: '',
        item_account_id: '',
        variant_id: null,
        serial_numbers_text: '',
      };
      this.errors = {};
      this.query = '';
      this.selectedItem = null;
      this.warehouses = [];
      this.itemIndex = 0;
      this.selectedParentItem = null;
      this.availableSerials = [];
      this.results = [];

      this.$nextTick(() => {
        this.$refs.itemSearch?.focus();
      });
    },

    async fetchWarehouses() {
      if (!this.selectedItem) {
        this.toast.warning("Please select item first.");
        return;
      }

      const itemId = this.selectedItem?.item_account_id || this.selectedParentItem?.item_account_id;
      if (!itemId) {
        this.toast.warning("Missing item ID.");
        return;
      }

      try {
        const response = await this.$axios.get(`${apiUrls.item_warehouse.get}${itemId}/`);
        this.warehouses = response.data.results;

        // render feather icons
        await this.$nextTick(() => {
          if (window.feather) feather.replace()
        })
      } catch (error) {
        console.error('Search error:', error)
        this.warehouses = [];
      }
    },

    async onChange(warehouseId) {
      if (!this.selectedItem) {
        this.toast.warning("Please select item first.");
        return;
      }

      try {
        const response = await this.$axios.get(apiUrls.item_warehouse_qty.get, {
          params: {
            item_id: this.selectedItem.item_account_id || this.selectedParentItem.item_account_id,
            warehouse_id: warehouseId,
            variant_id: this.selectedItem.variant_id || undefined
          }
        });

        this.warehouse_qty = response.data.total_qty || 0;

      } catch (error) {
        console.error(error);
        this.item.stock_quantity = 0;
      }
    },

    async fetchReceivingNo() {
      try {
        let res = await this.$axios.get(apiUrls.damage_receive_number.get, {
          params: { receiving_date: this.item.receiving_date }
        });
        this.item.receiving_no = res.data.receiving_no;
      } catch (err) {
        console.error(err);
      }
    },

    async resolveSerialIds(itemId, variantId, text, qty) {
      const serials = this.parseSerialNumbers(text);
      if (serials.length !== Number(qty)) {
        throw new Error(`Provide exactly ${qty} serial number(s).`);
      }
      const { data } = await this.$axios.get(apiUrls.serialized_units.get, {
        params: {
          item_id: itemId,
          variant_id: variantId || undefined,
          status: 'in_stock',
        },
      });
      const units = data.results || [];
      const bySerial = new Map(units.map((u) => [String(u.serial_number).toLowerCase(), u.id]));
      const ids = [];
      for (const serial of serials) {
        const id = bySerial.get(String(serial).toLowerCase());
        if (!id) {
          throw new Error(`Serial not found in stock: ${serial}`);
        }
        ids.push(id);
      }
      return ids;
    },

    async submitDamageItem() {
      if (this.saving) return;
      if (!this.addedItems.length) {
        this.toast.warning("Please add at least one Item before submitting.");
        return;
      }

      this.saving = true;
      try {
        const payload = this.addedItems.map(row => ({
          item: row.item_id || row.id || row.item,
          variant_id: row.variant_id || null,
          quantity: row.quantity,
          narration: row.narration,
          naration: row.narration,
          warehouse_id: row.warehouse_id,
          receiving_no: this.item.receiving_no,
          receiving_date: this.item.receiving_date,
          serial_ids: row.serial_ids || [],
        }));
        await this.$axios.post(apiUrls.damage_item.post, payload);

        await this.fetchReceivingNo();
        const updatedReceivingNo = this.item.receiving_no;
        this.toast.success("successfully submitted!");
        this.$router.push('/damage/item/list');
        this.$emit("create_success");

        // Reset form after save
        this.item = {
          item: null, narration: '', quantity: '',
          receiving_no: updatedReceivingNo, receiving_date: this.item.receiving_date, stock_quantity: '',
          warehouse_id: '', item_account_id: ''
        };
        this.selectedItem = null;
        this.addedItems = [];

      } catch (error) {
        this.toast.error(apiErrorMessage(error));
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
      } finally {
        this.saving = false;
      }
    },

    cancelForm() {
      this.$router.push('/damage/item/list');
    },

    removeItem(index) {
      this.addedItems.splice(index, 1);
    },

    resetForm() {
      // Reset form fields and errors
      this.item = {
        item: '', narration: '',
        receiving_no: '', receiving_date: '', stock_quantity: '', warehouse_id: '', item_account_id: ''
      };
      this.errors = {};
      this.errorMessage = "";
    }
  },

  watch: {
    'item.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.item.receiving_no = '';
      }
    },

    'item.warehouse_id'(newVal) {
      if (newVal) {
        this.onChange(newVal);
      }
      if (this.selectedItem?.item_type_code === 'WARRANTY') {
        this.loadAvailableSerials(this.selectedItem);
      }
    }
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300)
  },

};
</script>


<style scoped>
.damage-field-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 38px;
}

.damage-field-row-top {
  align-items: flex-start;
}

.damage-field-label {
  flex: 0 0 140px;
  width: 140px;
  margin: 0;
  padding-top: 0.45rem;
  font-size: 0.875rem;
  line-height: 1.2;
  white-space: nowrap;
}

.damage-field-control {
  flex: 1 1 auto;
  min-width: 0;
}

.damage-serial-picker {
  max-height: 160px;
  overflow-y: auto;
  background: #f8f9fa;
}

.search-input-with-clear {
  padding-right: 2.5rem;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 0.45rem;
  transform: translateY(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.65rem;
  height: 1.65rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #495057;
  color: #fff;
  line-height: 1;
}

.search-clear:hover {
  background: #212529;
  color: #fff;
}

.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}
</style>

